 
import { Checkbox, Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  DatePicker,
  Delete,
  EditableTable,
  PageTitleAndSubTitle,
  RadioButton,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { removeEmptyFields } from '../../../core-utils/helper-utils/riskRegisterHelper';
import { financialForensicsConstants } from '../../../core-utils/messages/financialForensicsConstants';
import moment from 'moment';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import { editableColumn } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/EditableTable';
import {
  CoverageType,
  emptyStoplossCoverage,
  emptyStoplossVendor,
  StopLossVendor,
  SubscriptionData,
  SubscriptionType,
} from '../../../pages/FinancialForensicsPage/types/StopLoss.types';

export interface AddStoplossModalProps {
  open: boolean;
  startDateDefault?: string;
  endDateDefault?: string;
  dataForPropsStopLoss: StopLossVendor;
  handleSubmit: (_data: any) => void;
  handleDelete: (_vendorId: any) => void;
  handleOpenSnackBar: any;
  handleCloseM: () => void;
}

const useStyles = makeStyles()({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: '676px',
    height: '760px',
    padding: '24px 28px 40px 32px',
    backgroundColor: patientAccessTheme.palette.status.white,
    border: 'none !important',
    position: 'relative',
  },
  button: {
    padding: '0px',
  },
  scroll: scrollBarStyles,
  flex: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
    alignItems: 'start',
    margin: '0px 16px 20px 0px',
  },
  service: {
    backgroundColor: patientAccessTheme.palette.grey[100],
    padding: '20px 20px 15px 20px',
    height: 'fit-content',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    overflowY: 'scroll',
    height: '588px',
  },
});

const AddStoplossModal: React.FC<AddStoplossModalProps> = observer(
  ({
    open,
    startDateDefault,
    endDateDefault,
    dataForPropsStopLoss,
    handleSubmit,
    handleDelete,
    handleCloseM,
  }) => {
    const { classes } = useStyles();
    const [coverageOptions, setCoverageOptions] = useState<any>([
      { name: 'Specific', checked: false },
      { name: 'Aggregate', checked: false },
    ]);

    const [specificCoverageOptions, setSpecificCoverageOptions] = useState<any>(
      [
        { name: 'Composite', checked: false },
        { name: 'Tiered by Subscription', checked: false },
      ],
    );

    const [data, setData] = useState<StopLossVendor>(
      emptyStoplossVendor(startDateDefault, endDateDefault),
    );

    //Constants
    const isCommonFieldsFilled =
      data.name &&
      data.policyStartYear &&
      data.policyEndYear &&
      data.coverage?.every(
        (item: any) =>
          item.coverage &&
          item.subscription.length > 0 &&
          item.subscription.every(
             
            (temp: any) => temp.premiumAmount && temp.unit,
          ),
      );

    //functions
    const handleClose = () => {
      setData(emptyStoplossVendor(startDateDefault, endDateDefault));
      handleCloseM();
    };

    const handleChange = (event: any, fieldName: string) => {
      const value = event.target.value as string;
      setData((prevData: any) => ({
        ...prevData,
        [fieldName.toLowerCase()]: value,
      }));
    };

    const handleCheckboxChange = (index: number) => {
      const updatedOptions = [...coverageOptions];
      updatedOptions[index].checked = !updatedOptions[index].checked;
      setCoverageOptions(updatedOptions);

      const selected = updatedOptions[index];
      if (selected.checked) {
        updateCoverageData(selected.name);
      } else {
        setData((prev) => ({
          ...prev,
          coverage: prev.coverage.filter((c) => c.coverage !== selected.name),
        }));
      }
    };

    const handleRadiobuttonChange = (index: number) => {
      const updatedOptions = specificCoverageOptions.map(
        (option: any, i: number) => ({
          ...option,
          checked: i === index,
        }),
      );
      setSpecificCoverageOptions(updatedOptions);

      const selectedOption = updatedOptions[index];
      if (selectedOption.checked) {
        updateCoverageData('Specific', selectedOption.name);
      }
    };

    const updateCoverageData = (
      type: CoverageType,
      option?: SubscriptionType,
    ) => {
      setData((prev) => {
        const existingIndex = prev.coverage.findIndex(
          (c) => c.coverage === type,
        );
        const updatedCoverage = emptyStoplossCoverage(type, option);
        const updatedCoverages = [...prev.coverage];

        if (existingIndex !== -1) {
          updatedCoverages[existingIndex] = updatedCoverage;
        } else {
          updatedCoverages.push(updatedCoverage);
        }

        return { ...prev, coverage: updatedCoverages };
      });
    };

    const getColumnsForCoverage = (coverageType: string): editableColumn[] => {
      const baseCols: editableColumn[] = [
        {
          key: 'premiumAmount',
          label: 'Premium Amount',
          editable: true,
          type: 'number',
        },
        {
          key: 'unit',
          label: 'Unit',
          editable: true,
          type: 'dropdown',
          options: ['Per Employee Per Month'],
          width: '280px',
        },
      ];

      if (coverageType === 'Specific') {
        return [
          {
            key: 'type',
            label: 'Subscription Type',
            editable: false,
            width: '160px',
          },
          ...baseCols,
        ];
      }

      return baseCols;
    };

    useEffect(() => {
      if (!open) {
        return;
      }

      const isEdit = dataForPropsStopLoss.vendorID !== '';

      if (isEdit) {
        setData(dataForPropsStopLoss);
        setCoverageOptions(
          ['Specific', 'Aggregate'].map((name) => ({
            name,
            checked: dataForPropsStopLoss.coverage.some(
              (c) => c.coverage === name,
            ),
          })),
        );
        setSpecificCoverageOptions(
          ['Composite', 'Tiered by Subscription'].map((name) => ({
            name,
            checked:
              dataForPropsStopLoss.coverage.find(
                (c) => c.coverage === 'Specific',
              )?.subscriptionType === name,
          })),
        );
      } else {
        setData(emptyStoplossVendor(startDateDefault, endDateDefault));
        setCoverageOptions([
          { name: 'Specific', checked: false },
          { name: 'Aggregate', checked: false },
        ]);
        setSpecificCoverageOptions([
          { name: 'Composite', checked: false },
          { name: 'Tiered by Subscription', checked: false },
        ]);
      }
    }, [open, dataForPropsStopLoss]);

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '30px',
              }}
            >
              <PageTitleAndSubTitle
                title={financialForensicsConstants.addStoploss}
                subTitle={''}
                subTitleStyles={{ width: '0px' }}
              />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            <Grid className={`${classes.scroll} ${classes.content}`}>
              <Grid
                className={`${classes.flex} ${classes.service}`}
                style={{ marginBottom: '30px' }}
              >
                <ReportCreateNewLabelField
                  label={financialForensicsConstants.name}
                  component={
                    <TextField
                      width="560px"
                      height="36px"
                      placeholder={financialForensicsConstants.name}
                      onChange={(event: any) => handleChange(event, 'name')}
                      value={data.name}
                      placeholderStyle={{
                        fontSize: '16px',
                      }}
                      error={!data.name}
                    />
                  }
                />
                <ReportCreateNewLabelField
                  label={financialForensicsConstants.policyStartYear}
                  component={
                    <DatePicker
                      width="270px"
                      handleChange={(value) => {
                        setData((prev: any) => ({
                          ...prev,
                          policyStartYear: moment(value)
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                        }));
                      }}
                      selectedDate={
                        data.policyStartYear
                          ? moment(data.policyStartYear)
                          : startDateDefault
                      }
                      format="yyyy-MM-dd"
                      placeholder={financialForensicsConstants.date_format}
                      error={!data.policyStartYear}
                      views={['year', 'month']}
                      minDate={undefined}
                      maxDate={undefined}
                    />
                  }
                />
                <ReportCreateNewLabelField
                  label={financialForensicsConstants.policyEndYear}
                  component={
                    <DatePicker
                      width="270px"
                      handleChange={(value) => {
                        setData((prev: any) => ({
                          ...prev,
                          policyEndYear: moment(value)
                            .endOf('month')
                            .format('YYYY-MM-DD'),
                        }));
                      }}
                      selectedDate={
                        data.policyEndYear
                          ? moment(data.policyEndYear)
                          : endDateDefault
                      }
                      format="yyyy-MM-dd"
                      placeholder={financialForensicsConstants.date_format}
                      error={!data.policyEndYear}
                      views={['year', 'month']}
                      minDate={undefined}
                      maxDate={undefined}
                    />
                  }
                />
              </Grid>
              <Typography
                textColor={patientAccessTheme.palette.text.highemphasis}
                fontSize="12px"
                lineHeight="12px"
                fontType={'medium'}
              >
                {financialForensicsConstants.coverage}
              </Typography>
              <div style={{ display: 'flex', gap: '20px' }}>
                {coverageOptions.map((option: any, index: number) => (
                  <label
                    key={option.name}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Checkbox
                      checked={option.checked}
                      onChange={() => handleCheckboxChange(index)}
                      color="primary"
                      icon={
                        <CheckBoxOutlineBlankOutlinedIcon
                          style={{
                            color: patientAccessTheme.palette.checkbox?.border1,
                          }}
                        />
                      }
                    />
                    <span style={{ marginLeft: '6px' }}>
                      <Typography
                        textColor={patientAccessTheme.palette.text.highemphasis}
                        fontSize="12px"
                        lineHeight="12px"
                        fontType={'semiBold'}
                      >
                        {option.name}
                      </Typography>
                    </span>
                  </label>
                ))}
              </div>
              {coverageOptions.map(
                (temp: any) =>
                  temp.checked && (
                    <>
                      <Typography
                        textColor={patientAccessTheme.palette.text.lowemphasis}
                        fontSize="12px"
                        lineHeight="12px"
                        fontType="medium"
                        style={{ marginTop: '20px' }}
                      >
                        {temp.name} Coverage
                      </Typography>

                      {temp.name === 'Specific' && (
                        <div
                          style={{
                            display: 'flex',
                            gap: '20px',
                            marginBottom: '10px',
                          }}
                        >
                          {specificCoverageOptions.map(
                            (option: any, index: number) => (
                              <label
                                key={option.name}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <RadioButton
                                  color={
                                    patientAccessTheme.palette.primary[400]
                                  }
                                  checked={option.checked}
                                  handleChange={() =>
                                    handleRadiobuttonChange(index)
                                  }
                                />
                                <span style={{ marginLeft: '6px' }}>
                                  <Typography
                                    textColor={
                                      patientAccessTheme.palette.text
                                        .highemphasis
                                    }
                                    fontSize="12px"
                                    lineHeight="12px"
                                    fontType="semiBold"
                                  >
                                    {option.name}
                                  </Typography>
                                </span>
                              </label>
                            ),
                          )}
                        </div>
                      )}

                      {data.coverage
                        .filter((c) => c.coverage === temp.name)
                        .map((coverageItem, coverageIndex) => {
                          if (!coverageItem.subscription?.length) {
                            return null;
                          }

                          const tableCols = getColumnsForCoverage(temp.name);

                          const handleCoverageTableChange = (
                            updatedSubscription: SubscriptionData[],
                          ) => {
                            const updated = [...data.coverage];
                            const originalIndex = data.coverage.findIndex(
                              (item) => item === coverageItem,
                            );
                            if (originalIndex !== -1) {
                              updated[
                                originalIndex
                              ].subscription = updatedSubscription;
                              setData({ ...data, coverage: updated });
                            }
                          };

                          return (
                            <EditableTable
                              key={`${temp.name.toLowerCase()}-${coverageIndex}`}
                              data={coverageItem.subscription}
                              columns={tableCols}
                              onChange={handleCoverageTableChange}
                              containerStyles={{
                                overflow: 'visible',
                                height: 'auto',
                                overflowY: 'visible',
                                width: '590px',
                              }}
                              bodyCellStyles={{
                                padding: '0.5rem',
                                '& input[type=number]': {
                                  MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                              }}
                            />
                          );
                        })}
                    </>
                  ),
              )}
            </Grid>

            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 40px 40px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <>
                    <Typography
                      textColor={patientAccessTheme.palette.status.error}
                      fontSize="12px"
                      lineHeight="12px"
                      fontType={'medium'}
                    >
                      {financialForensicsConstants.delete_vendor}
                    </Typography>
                    <Delete stroke={patientAccessTheme.palette.status.error} />
                  </>
                }
                variant="text"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  handleDelete(data.vendorID);
                  handleClose();
                }}
              />
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary.main}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {financialForensicsConstants.Save_update}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  const cleanedData = removeEmptyFields(data);
                  handleSubmit({
                    ...cleanedData,
                  });
                  handleClose();
                }}
              />
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.status.white}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {financialForensicsConstants.submit}
                  </Typography>
                }
                variant="contained"
                color="primary"
                height="36px"
                width="180px"
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  const cleanedData = removeEmptyFields(data);
                  handleSubmit({
                    ...cleanedData,
                  });
                  handleClose();
                }}
                disabled={!isCommonFieldsFilled}
              />
            </Grid>
          </Paper>
        </Modal>
      </>
    );
  },
);

export default AddStoplossModal;
